import type { GateStatus } from './types';

// The React-free core of the gate registry (see gate-registry.ts, which
// re-exports all of this and adds the hooks). It holds the one live
// GateClient and its subscribers. Kept free of React so plain code can read
// gates without pulling React in: the analytics facade reads gates from here,
// and the headless crochet engine, which runs in a Worker, imports the facade
// (test/headless-import-closure.test.ts).

export type GateClient = {
    checkGate(name: string): boolean;
    /** The client's loading status. Gates read false until 'Ready', so a false
     *  checkGate before then means "unknown", not "off". Optional so fakes
     *  without it count as ready. */
    readonly loadingStatus?: 'Uninitialized' | 'Loading' | 'Ready';
    on(event: 'values_updated', cb: () => void): void;
    off(event: 'values_updated', cb: () => void): void;
};

let liveGateClient: GateClient | null = null;
const gateSubscribers = new Set<() => void>();
const notifyGateSubscribers = () => { for (const cb of gateSubscribers) cb(); };

/**
 * Register (or clear) the live client: the app's single gate source. Every
 * subscriber is notified, and then again on each `values_updated`.
 */
export function setLiveGateClient(client: GateClient | null): void {
    if (liveGateClient) liveGateClient.off('values_updated', notifyGateSubscribers);
    liveGateClient = client;
    if (client) client.on('values_updated', notifyGateSubscribers);
    notifyGateSubscribers();
}

/** Subscribe to client (re)registration and `values_updated`; returns the unsubscribe. */
export function subscribeToGates(cb: () => void): () => void {
    gateSubscribers.add(cb);
    return () => { gateSubscribers.delete(cb); };
}

/**
 * Non-reactive gate read for plain (non-React) callers: the live client's
 * answer, or `false` when no client is registered.
 */
export function readGateValue(name: string): boolean {
    return liveGateClient?.checkGate(name) ?? false;
}

/**
 * Tri-state gate read for callers that must tell "not evaluated yet" from
 * "off" (e.g. the theme, which holds a cached choice until the gate resolves).
 * With no client registered nothing will ever evaluate, so every gate is off.
 */
export function readGateStatus(name: string): GateStatus {
    if (liveGateClient == null) return 'off';
    const status = liveGateClient.loadingStatus;
    if (status != null && status !== 'Ready') return 'pending';
    return liveGateClient.checkGate(name) ? 'on' : 'off';
}
